import type { MRT_ColumnDef } from 'material-react-table';
import { BenefitActivationDTO, BenefitDTOBenefitEnum, EmployeeDTO, TenantDTO } from 'probonio-shared-ui/api';
import { useTenant } from 'probonio-shared-ui/module/me';
import { EXTENSIONS } from 'probonio-shared-ui/registry';
import { ExtensionInterface } from 'probonio-shared-ui/registry/extension-definition';
import { SortByDef } from '../../component/table/sortMapper';
import { BenefitMetadataExtensionInterface } from './benefit-metadata.extension.interface';

export interface BenefitAttribute {
  id: string;
  accessorFn: (value: BenefitActivationDTO) => unknown;
  header: string;
  size?: number;
  maxSize?: number;
  renderer?: (value: unknown) => React.ReactNode;
}

export abstract class BenefitColumnsExtensionInterface extends ExtensionInterface {
  abstract getBenefitAttributes: (context?: { tenant?: TenantDTO }) => BenefitAttribute[];
}

export function useEmployeeBenefitColumns(benefit: BenefitDTOBenefitEnum) {
  const benefitAttributes = useBenefitAttributes(benefit);
  if (!benefitAttributes) {
    return undefined;
  }
  return benefitAttributes.map(attribute => {
    return {
      id: attribute.id,
      accessorFn: employee => employee?.benefitActivation && attribute.accessorFn(employee?.benefitActivation),
      header: attribute.header,
      size: attribute.size,
      maxSize: attribute.maxSize,
      enableSorting: false,
      Cell: attribute.renderer && (({ cell }) => attribute.renderer?.(cell.getValue())),
    } satisfies MRT_ColumnDef<EmployeeDTO> & SortByDef;
  });
}

export function useBenefitAttributes(benefit: BenefitDTOBenefitEnum) {
  const { tenant } = useTenant();
  const extensionKeyForBenefit = EXTENSIONS.getKeyedProviders(BenefitMetadataExtensionInterface).find(
    ({ provider }) => provider.benefitId === benefit,
  )?.key;
  return EXTENSIONS.getProviderByKey(extensionKeyForBenefit, BenefitColumnsExtensionInterface)?.getBenefitAttributes({ tenant });
}
